import IMultiSelectWithCheckBox from '@components/multi-select/IMultiSelectWithCheckBox';
import StyledAutocomplete from '@components/styled-autocomplete';
import useStyledAutocomplete, { TypeReturn } from '@components/styled-autocomplete/useStyledAutocomplete';
import { Location } from '@EcamModel/model';
import { Zones } from '@EcamModel/model/Zones';
import { Stack } from '@mui/material';
import { Filter } from '@pages/cameras/list';
import { search } from '@pages/cameras/list/helpers';
import { useState } from 'react';
import { LoadingProps } from '..';
import { cameraPriorityTask, cameraStatusTask } from '../config';

export type PropsFilterChild = {
    filter: Partial<Filter>;
    locationAutocomplete?: TypeReturn<
        Location,
        {
            search: any;
        }
    >;
    loadingState: boolean;
    isShowTaskManagement?: boolean;
    onSetPartialFilter: (filter: Partial<Filter>) => void;
};

export default function FilterChildForTaskManagement(props: PropsFilterChild) {
    const { filter, locationAutocomplete, isShowTaskManagement, onSetPartialFilter } = props;
    const [zones, setZones] = useState<Zones[]>([]);

    const statusAutocomplete = useStyledAutocomplete({
        list: {
            options: cameraStatusTask,
            isFiltered: (option, searchText) => search(option.Name, searchText),
        },
    });

    const priorityStatusAutocomplete = useStyledAutocomplete({
        list: {
            options: cameraPriorityTask,
            isFiltered: (option, searchText) => search(option.Name, searchText),
        },
    });

    const onChangeLocation = (l?: Location) => {
        onSetPartialFilter({ location: l, zones: l ? l.Zones : undefined });
        setZones(l ? l.Zones! : []);
    };

    return (
        <Stack direction={'column'} spacing={2} sx={{ flexGrow: 1 }}>
            {!isShowTaskManagement && (
                <Stack
                    direction={'row'}
                    spacing={{ xs: 0, sm: 2 }}
                    rowGap={{ xs: 2, sm: 0 }}
                    width={'100%'}
                    flexWrap={{ xs: 'wrap', sm: 'inherit' }}
                >
                    <Stack width={{ xs: '100%', sm: '50%' }} zIndex={{ xs: 14, md: 8 }}>
                        <StyledAutocomplete
                            {...locationAutocomplete!}
                            getOptionLabel={(o) => o.Name}
                            label="Location"
                            value={filter.location}
                            placeholder="All locations"
                            onChange={onChangeLocation}
                            disabled={props.loadingState}
                        />
                    </Stack>

                    <Stack width={{ xs: '100%', sm: '50%' }} zIndex={{ xs: 13, md: 8 }}>
                        <IMultiSelectWithCheckBox
                            limitTags={3}
                            isSelectZone
                            label={'Zone'}
                            keyId={'Id'}
                            keyLabel={'PublicName'}
                            listData={zones}
                            isDisable={!zones.length || filter.location === undefined || props.loadingState}
                            value={filter.zones ?? []}
                            setFilter={(option) => {
                                onSetPartialFilter({ zones: option.length ? option : undefined });
                            }}
                            chipSize={150}
                        />
                    </Stack>
                </Stack>
            )}

            <Stack
                direction={'row'}
                spacing={{ xs: 0, sm: 2 }}
                rowGap={{ xs: 2, sm: 0 }}
                width={'100%'}
                flexWrap={{ xs: 'wrap', sm: 'inherit' }}
            >
                <Stack width={{ xs: '100%', sm: '50%' }} zIndex={{ xs: 12, md: 7 }}>
                    <StyledAutocomplete
                        {...statusAutocomplete}
                        getOptionLabel={(o) => o.Name}
                        label="Status"
                        value={filter.status}
                        placeholder="All status"
                        disabled={props.loadingState}
                        onChange={(o) => {
                            onSetPartialFilter({ status: o });
                        }}
                    />
                </Stack>

                <Stack width={{ xs: '100%', sm: '50%' }} zIndex={{ xs: 11, md: 7 }}>
                    <StyledAutocomplete
                        {...priorityStatusAutocomplete}
                        getOptionLabel={(o) => o.Name}
                        label="Priority"
                        value={filter.taskPriorities}
                        placeholder="All priorities"
                        disabled={props.loadingState}
                        onChange={(p) => {
                            onSetPartialFilter({ taskPriorities: p });
                        }}
                    />
                </Stack>
            </Stack>
        </Stack>
    );
}
